"use client"

import type { SmartAnalysisTierCounts } from '@/lib/smart-analysis-tiers'
import { cn } from '@/lib/utils'

const SEGMENT_MIN_WIDTH_PX = 8

type SegmentKey = keyof SmartAnalysisTierCounts

const SEGMENT_CONFIG: {
  key: SegmentKey
  label: string
  barClassName: string
  dotClassName: string
}[] = [
  {
    key: 'top',
    label: 'Strong matches',
    barClassName: 'bg-success',
    dotClassName: 'bg-success',
  },
  {
    key: 'compelling',
    label: 'Good matches',
    barClassName: 'bg-muted-foreground/60',
    dotClassName: 'bg-muted-foreground/70',
  },
  {
    key: 'precluded',
    label: 'Not recommended',
    barClassName: 'bg-muted-foreground/25',
    dotClassName: 'bg-muted-foreground/40',
  },
]

export type SmartAnalysisSegmentedOutcomeBarProps = {
  counts: SmartAnalysisTierCounts
  className?: string
}

/** Segmented outcome bar — proportional flex segments with legend labels. */
export function SmartAnalysisSegmentedOutcomeBar({
  counts,
  className,
}: SmartAnalysisSegmentedOutcomeBarProps) {
  const total = counts.top + counts.compelling + counts.precluded
  if (total === 0) return null

  const activeSegments = SEGMENT_CONFIG.filter(({ key }) => counts[key] > 0)

  return (
    <div className={cn('space-y-2', className)} role="img" aria-label="Analysis outcome distribution">
      <div
        className="flex h-2.5 w-full overflow-hidden rounded-full bg-muted"
        aria-hidden
      >
        {activeSegments.map(({ key, barClassName }) => {
          const value = counts[key]
          const flexGrow = value / total
          return (
            <div
              key={key}
              className={cn('h-full shrink-0 transition-all', barClassName)}
              style={{
                flexGrow,
                flexBasis: `${Math.max((value / total) * 100, (SEGMENT_MIN_WIDTH_PX / 320) * 100)}%`,
                minWidth: value > 0 ? SEGMENT_MIN_WIDTH_PX : 0,
              }}
            />
          )
        })}
      </div>
      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
        {SEGMENT_CONFIG.map(({ key, label, dotClassName }) => {
          const value = counts[key]
          if (value === 0) return null
          return (
            <li key={key} className="flex items-center gap-1.5 tabular-nums">
              <span
                className={cn('size-2 shrink-0 rounded-full', dotClassName)}
                aria-hidden
              />
              <span>
                <span className="font-medium text-foreground">{value}</span>{' '}
                {label.toLowerCase()}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
